package com.evoapps.evoshift.domain.usecase.finance

import com.evoapps.evoshift.domain.explain.Breakdown
import com.evoapps.evoshift.domain.explain.Figure
import com.evoapps.evoshift.domain.explain.Sign
import com.evoapps.evoshift.domain.explain.Term
import com.evoapps.evoshift.domain.explain.TermKind
import com.evoapps.evoshift.domain.market.Market
import com.evoapps.evoshift.domain.market.payroll.MonthPayFactors
import com.evoapps.evoshift.domain.market.payroll.PayrollRules
import com.evoapps.evoshift.domain.market.payroll.PayslipInput
import com.evoapps.evoshift.domain.market.payroll.WorkplacePayrollSettings
import com.evoapps.evoshift.domain.model.Absence
import com.evoapps.evoshift.domain.model.Adjustment
import com.evoapps.evoshift.domain.model.AdjustmentType
import com.evoapps.evoshift.domain.model.BreakPolicy
import com.evoapps.evoshift.domain.model.ClockReviewSettings
import com.evoapps.evoshift.domain.model.CompensationModel
import com.evoapps.evoshift.domain.model.FinancialEngagement
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.ShiftFinancials
import com.evoapps.evoshift.domain.model.WorkplaceSettings
import com.evoapps.evoshift.domain.model.paidSpan
import com.evoapps.evoshift.domain.repository.AbsenceRepository
import com.evoapps.evoshift.domain.repository.FinancialRepository
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.repository.UserRepository
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import com.evoapps.evoshift.domain.usecase.CalculateShiftCostUseCase
import kotlinx.coroutines.flow.first
import kotlin.math.abs
import kotlin.math.round

/**
 * What a person's own pay estimate for one roster month is made of, all of it readable by the
 * person themselves (firestore.rules): their own cost snapshots, their own pay terms, whether they
 * are paid at the workplace, their own adjustments and absences, and the workplace's settings.
 */
data class OwnPayFacts(
    val costs: List<ShiftFinancials>,
    val engagement: FinancialEngagement?,
    val isPaid: Boolean,
    val adjustments: List<Adjustment>,
    val payroll: WorkplacePayrollSettings,
    /** SH-57: the person's own absences (approved sick leave and annual leave count). */
    val absences: List<Absence> = emptyList(),
    /** SH-116: unpaid break time is not worked. */
    val breakPolicy: BreakPolicy = BreakPolicy(),
    /** SH-134: whether the payable time counts from the planned start. */
    val clockReview: ClockReviewSettings = ClockReviewSettings(),
    /** SH-165: the workplace's settings, to price a shift the person has no cost snapshot of (one they would take). */
    val settings: WorkplaceSettings? = null,
    /** SH-223: the person's own work requests (approved overtime and work events are paid work). */
    val workRequests: List<com.evoapps.evoshift.domain.model.WorkRequest> = emptyList()
)

/** A person's own estimate for a roster month: the gross, the take-home pay (null without pay terms) and its factors. */
data class OwnMonthPay(
    val gross: Double,
    val net: Double?,
    /** SH-57: overtime, Sunday and holiday work, the minimum wage, leave; null without pay terms. */
    val factors: MonthPayFactors?,
    /** What the salary or the shifts' cost snapshots alone come to (before bonuses and the factors). */
    val baseGross: Double
)

/** Whether a shift would leave the person's month (given away, swapped) or join it (taken). */
enum class ShiftPayChangeKind { REMOVE, ADD }

/**
 * SH-165: how giving away, swapping or taking one shift changes the person's own estimated pay
 * for its month: [before] and [after] are the same figure as the dashboard's estimate (the take-home
 * pay), [delta] = after − before.
 */
data class ShiftPayChange(
    val shiftId: String,
    val kind: ShiftPayChangeKind,
    val year: Int,
    val month: Int,
    val before: Double,
    val after: Double,
    /** A monthly salary: the shift itself does not change the pay (only allowances or overtime might). */
    val salaried: Boolean,
    /** How the change was worked out: the shift's pay, what changes with it, and the taxes on the difference. */
    val calculation: Breakdown
) {
    val delta: Double get() = round((after - before) * 100) / 100.0
    /** Too small to show: a salary the shift does not change. */
    val isUnchanged: Boolean get() = abs(delta) < Breakdown.MONEY_EPSILON
}

/**
 * The person's own pay estimate (the dashboard's card, SH-57/SH-78/SH-149) as a pure function, and
 * SH-165's change of it for one shift more or less, worked out by the same rules.
 */
object OwnMonthPayEstimate {
    private fun round2(value: Double) = round(value * 100) / 100.0

    /**
     * The estimate for [rosterId] from [myShifts] (the person's published shifts, all months and
     * workplaces; those of the settlement period and the year before count) and [facts].
     */
    fun of(userId: String, rosterId: String, myShifts: List<Shift>, facts: OwnPayFacts): OwnMonthPay {
        val workplaceId = RosterIds.workplaceIdOf(rosterId)
        val (rosterYear, rosterMonth) = RosterIds.yearMonthOf(rosterId)
        val engagement = facts.engagement
        val adjustments = facts.adjustments
        val rosterShifts = myShifts.filter { it.rosterId == rosterId }

        // SH-57: approved leave of the year; a shift on a day of leave nobody clocked was not worked.
        val leave = MonthWork.leaveDays(facts.absences, userId, workplaceId, rosterYear, rosterMonth)
        val monthFirst = MonthWork.firstDay(rosterYear, rosterMonth)
        val leaveThisMonth = leave.filter { it.date >= monthFirst }

        // Earnings: the shifts' cost snapshots (clocked pay once clocked), or the salary.
        val publishedIds = rosterShifts.filterNot { MonthWork.isOnLeave(it, leaveThisMonth) }.mapTo(HashSet()) { it.id }
        val myFinancials = facts.costs.filter { it.shiftId in publishedIds }
        // The terms in force at the end of the month (a change during it is paid pro rata in the factors).
        val salary = engagement?.compensationOn(MonthWork.lastDay(rosterYear, rosterMonth).toString()) as? CompensationModel.MonthlyFixed
        val rawEarnings = salary?.let { it.baseSalary + myFinancials.sumOf { f -> f.costs.nightAllowance } }
            ?: myFinancials.sumOf { it.costs.actualPayout }
        fun sum(type: AdjustmentType) = adjustments.filter { it.type == type }.sumOf { it.amount }
        // SH-57: overtime against the settlement period, Sunday and holiday work, the minimum wage;
        // from the person's published shifts here in the period up to this month.
        val periodMonths = facts.payroll.settlementMonths(rosterMonth)
        val periodShifts = myShifts.filter { shift ->
            RosterIds.workplaceIdOf(shift.rosterId) == workplaceId &&
                RosterIds.yearMonthOf(shift.rosterId).let { (y, m) -> y == rosterYear && m in periodMonths && m <= rosterMonth } &&
                !(shift.rosterId == rosterId && MonthWork.isOnLeave(shift, leaveThisMonth))
        }
        // The 12 months before, for the sick-pay base and the holiday-pay averages (only when on leave).
        val earlierIds = if (leaveThisMonth.isEmpty()) emptySet() else MonthWork.earlierRosterIds(workplaceId, rosterYear, rosterMonth).toSet()
        val earlierWork = myShifts.filter { it.rosterId in earlierIds }.mapNotNull { it.paidSpan(facts.breakPolicy, facts.clockReview) }
        // SH-223: approved overtime and work events outside the shifts.
        val extraWork = MonthWork.extraWork(
            facts.workRequests, userId, workplaceId, rosterYear, rosterMonth,
            workedShiftDates = rosterShifts.filter { it.id in publishedIds }.map { it.date }.toSet(),
            isWorkingDay = { PayrollRules.of(Market.POLAND).isLeaveDay(it) }
        )
        val factors = engagement?.takeIf { facts.isPaid }?.let {
            PayrollRules.of(Market.POLAND).monthFactors(
                MonthWork.input(
                    rosterYear, rosterMonth, it, periodShifts.mapNotNull { shift -> shift.paidSpan(facts.breakPolicy, facts.clockReview) }, facts.payroll,
                    sum(AdjustmentType.BONUS), leave, earlierWork, extraWork
                )
            )
        }
        // Bonuses are taxed with the pay; deductions come off and refunds on top of the take-home pay.
        val roundedEarnings = round2(rawEarnings + sum(AdjustmentType.BONUS) + (factors?.extraGross ?: 0.0))
        // Net does not depend on employer-level settings; one market (Poland) so far.
        val net = engagement?.takeIf { roundedEarnings > 0 }?.let {
            val takeHome = PayrollRules.of(Market.POLAND).payslip(
                PayslipInput(
                    rosterYear, rosterMonth, it.contract, it.profileOn(MonthWork.lastDay(rosterYear, rosterMonth).toString()),
                    roundedEarnings, WorkplacePayrollSettings(),
                    contributionFreeGross = factors?.contributionFreeGross ?: 0.0,
                    paidByOthers = factors?.paidByZus ?: 0.0
                ),
                // SH-149: the payroll facts of each day worked (a status that started or ended this month).
                MonthWork.profileShares(it, rosterYear, rosterMonth, myFinancials.groupBy({ f -> f.date }, { f -> f.costs.actualPayout }).mapValues { e -> e.value.sum() })
            ).net
            round2(takeHome - sum(AdjustmentType.DEDUCTION) + sum(AdjustmentType.EXPENSE_REFUND))
        }
        return OwnMonthPay(roundedEarnings, net, factors, round2(rawEarnings))
    }

    /**
     * SH-165: the change of the person's estimate for [shift]'s month when it leaves it ([kind]
     * REMOVE: given away or swapped) or joins it (ADD: taken from the marketplace). A shift the
     * person has no cost snapshot of is priced with their own pay terms ([CalculateShiftCostUseCase.price]).
     * Null without pay terms the person can read, when they are not paid at the workplace, or when
     * the shift cannot be priced: then nothing is shown.
     */
    fun change(userId: String, shift: Shift, kind: ShiftPayChangeKind, myShifts: List<Shift>, facts: OwnPayFacts): ShiftPayChange? {
        val engagement = facts.engagement ?: return null
        if (!facts.isPaid || shift.rosterId.isEmpty()) return null
        val rosterId = shift.rosterId
        val (year, month) = RosterIds.yearMonthOf(rosterId)
        val mine = shift.copy(userId = userId, actualUserId = null, swapRequest = null, isPublished = true, isOpen = false)
        val others = myShifts.filter { it.id != shift.id }
        val snapshot = facts.costs.find { it.shiftId == shift.id && it.userId == userId }
            ?: priced(mine, userId, engagement, facts, myShifts)
            ?: return null
        val withShift = facts.copy(costs = facts.costs.filter { it.shiftId != shift.id } + snapshot)
        val withoutShift = facts.copy(costs = facts.costs.filter { it.shiftId != shift.id })
        val with = of(userId, rosterId, others + mine, withShift)
        val without = of(userId, rosterId, others, withoutShift)
        val (beforePay, afterPay) = if (kind == ShiftPayChangeKind.REMOVE) with to without else without to with
        val before = beforePay.net ?: 0.0
        val after = afterPay.net ?: 0.0
        val salaried = engagement.compensationOn(MonthWork.lastDay(year, month).toString()) is CompensationModel.MonthlyFixed

        // The terms: the shift's own pay (a salary's allowance only), what the month's supplements
        // gain or lose with it, and the taxes and contributions on the difference.
        val sign = if (kind == ShiftPayChangeKind.ADD) Sign.PLUS else Sign.MINUS
        val baseChange = afterPay.baseGross - beforePay.baseGross
        val factorsChange = (afterPay.gross - afterPay.baseGross) - (beforePay.gross - beforePay.baseGross)
        val grossChange = afterPay.gross - beforePay.gross
        val delta = after - before
        val terms = buildList {
            if (salaried) {
                add(Term(TermKind.BASE_PAY, 0.0, Sign.NOT_COUNTED))
                if (abs(baseChange) >= Breakdown.MONEY_EPSILON) add(signed(TermKind.NIGHT_ALLOWANCE, baseChange))
            } else {
                add(
                    Term(
                        TermKind.SHIFT_GROSS, abs(baseChange), sign,
                        hours = snapshot.ratesSnapshot.plannedHours.takeIf { it > 0 },
                        rate = snapshot.ratesSnapshot.rateUsed.takeIf { it > 0 }
                    )
                )
            }
            if (abs(factorsChange) >= Breakdown.MONEY_EPSILON) add(signed(TermKind.SUPPLEMENTS_CHANGE, factorsChange))
            val taxes = delta - grossChange
            if (abs(taxes) >= Breakdown.MONEY_EPSILON) add(signed(TermKind.TAXES_AND_CONTRIBUTIONS, taxes))
        }
        return ShiftPayChange(
            shiftId = shift.id,
            kind = kind,
            year = year,
            month = month,
            before = before,
            after = after,
            salaried = salaried,
            calculation = Breakdown.of(Figure.PAY_CHANGE, null, round2(delta), terms)
        )
    }

    private fun signed(kind: TermKind, amount: Double) = Term(kind, abs(amount), if (amount < 0) Sign.MINUS else Sign.PLUS)

    /** [shift] priced with the person's own terms; null without the workplace's settings or with unreadable times. */
    private fun priced(shift: Shift, userId: String, engagement: FinancialEngagement, facts: OwnPayFacts, myShifts: List<Shift>): ShiftFinancials? {
        val settings = facts.settings ?: return null
        val monthHours = (myShifts.filter { it.rosterId == shift.rosterId && it.id != shift.id } + shift)
            .sumOf { com.evoapps.evoshift.domain.util.calculateDurationHours(it.plannedTime.start, it.plannedTime.end) }
        return runCatching { CalculateShiftCostUseCase.price(shift, userId, engagement, settings, monthHours) }.getOrNull()
    }
}

/**
 * SH-165: the person's own pay facts for a roster month, read once (their own data only), and the
 * change one shift more or less makes to the estimate ([OwnMonthPayEstimate.change]).
 */
class GetOwnPayFactsUseCase(
    private val shiftRepository: ShiftRepository,
    private val financialRepository: FinancialRepository,
    private val userRepository: UserRepository,
    private val workplaceRepository: WorkplaceRepository,
    private val absenceRepository: AbsenceRepository? = null,
    /** SH-223: the person's own work requests (overtime, work events). */
    private val workRequestRepository: com.evoapps.evoshift.domain.repository.WorkRequestRepository? = null
) {
    /** The facts and the person's published shifts (all months); null without pay terms they can read. */
    suspend operator fun invoke(userId: String, rosterId: String): Pair<OwnPayFacts, List<Shift>>? {
        val workplaceId = RosterIds.workplaceIdOf(rosterId)
        val engagement = userRepository.getSensitiveUserData(userId).getOrNull()?.financialEngagements?.get(workplaceId) ?: return null
        val facts = load(userId, rosterId, engagement)
        val shifts = runCatching { shiftRepository.observeShiftsForUser(userId, "2020-01-01").first() }.getOrDefault(emptyList())
            .filter { it.isPublished }
        return facts to shifts
    }

    /** The facts of [rosterId] for [userId]; [engagement] already read, else read here. */
    suspend fun load(userId: String, rosterId: String, engagement: FinancialEngagement? = null): OwnPayFacts {
        val workplaceId = RosterIds.workplaceIdOf(rosterId)
        val costs = financialRepository.getShiftFinancialsForUser(rosterId, userId).getOrDefault(emptyList())
        val terms = engagement ?: userRepository.getSensitiveUserData(userId).getOrNull()?.financialEngagements?.get(workplaceId)
        val workplace = workplaceRepository.getWorkplace(workplaceId).getOrNull()
        // SH-232: paid work on at least one day of the month (its unpaid days have no cost snapshot).
        val isPaid = workplace?.let { w ->
            val (year, month) = RosterIds.yearMonthOf(rosterId)
            w.isPaidIn(userId, com.evoapps.evoshift.domain.budget.LabourInclusion.monthOf(year, month))
        } ?: true
        // Only the person's own (the query and firestore.rules both say so).
        val adjustments = financialRepository.getAdjustmentsForUser(rosterId, userId).getOrDefault(emptyList())
            .filter { it.userId == userId }
            .sortedWith(compareBy({ it.date }, { it.metadata.createdAt }))
        // Only their own (firestore.rules let the person read their own absences).
        val absences = absenceRepository?.getAbsencesForUser(userId)?.getOrNull().orEmpty().filter { it.userId == userId }
        return OwnPayFacts(
            costs, terms, isPaid, adjustments, workplace?.settings?.payroll ?: WorkplacePayrollSettings(), absences,
            workplace?.settings?.breakPolicy ?: BreakPolicy(), workplace?.settings?.clockReview ?: ClockReviewSettings(),
            workplace?.settings,
            workRequestRepository?.forUser(userId)?.getOrNull().orEmpty().filter { it.workplaceId == workplaceId }
        )
    }
}
